import { useMemo, useState } from "react";

import { Key, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LicenseRequestRow } from "@/features/licenses/components/LicenseRequestRow";
import { useLicenseRequests } from "@/features/licenses/hooks/useLicenses";
import type { LicenseRequestStatus } from "@/features/licenses/schemas/license";
import { cn } from "@/shared/lib/utils";

type StatusFilter = LicenseRequestStatus | "all";

interface LicenseRequestsQueueProps {
  /** Si se pasa, limita a un usuario (dialog). */
  userId?: string;
  /** Oculta el h1 de página (útil dentro de dialog). */
  hideHeader?: boolean;
  className?: string;
}

/**
 * Cola de license_requests con filtro de status y búsqueda.
 */
export function LicenseRequestsQueue({
  userId,
  hideHeader = false,
  className,
}: LicenseRequestsQueueProps) {
  const [status, setStatus] = useState<StatusFilter>("pending");
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useLicenseRequests({
    status,
    userId,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (r) =>
        r.clientName.toLowerCase().includes(q) ||
        r.machineId.toLowerCase().includes(q) ||
        r.softwareType.toLowerCase().includes(q) ||
        r.userId.toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div className={cn("space-y-4", className)}>
      {!hideHeader && (
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Key className="h-6 w-6" aria-hidden />
              Solicitudes de licencia
            </h1>
            <p className="text-sm text-muted-foreground">
              Cola{" "}
              <code className="text-xs">license_requests</code>
              {userId ? " de este centro" : " global"}. Al aprobar se genera la
              licencia firmada.
            </p>
          </div>
        </header>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap rounded-lg border bg-muted/10 p-2.5">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar centro, machineId, software…"
          className="max-w-sm bg-background"
          aria-label="Buscar solicitudes"
        />
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as StatusFilter)}
        >
          <SelectTrigger className="w-[180px] bg-background">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="approved">Aprobadas</SelectItem>
            <SelectItem value="rejected">Rechazadas</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error.message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12" role="status">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Cargando solicitudes</span>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-10">
          No hay solicitudes
          {status === "pending" ? " pendientes" : ""}.
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((req) => (
            <li key={req.id}>
              <LicenseRequestRow request={req} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
